<template>
  <template v-if="canManage">
    <div v-if="loadingAccount" class="flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" width="2" size="24" />
    </div>
    <template v-else>
      <v-form
        id="company-information-form"
        ref="form"
        :disabled="updating || !canEditKyc"
        class="flex flex-col gap-6"
        validate-on="invalid-input lazy"
        @submit.prevent="save"
      >
        <OpeningCard :title="t('treezorOnboarding.sections.legal')">
          <v-row :gap="[32, 24]">
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.legalRegistrationNumber"
                :error-messages="missingMessage('legalRegistrationNumber')"
              >
                <template #label>
                  {{
                    t("treezorOnboarding.legalRegistrationNumber") +
                    (formData.country === "FR" ? " (SIREN)" : "")
                  }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.legalName"
                maxlength="200"
                :error-messages="missingMessage('legalName')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalName") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.legalForm"
                :items="legalFormItems"
                :error-messages="missingMessage('legalForm')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalForm") }}
                  <span class="text-error">*</span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.legalRegistrationDate"
                type="date"
                :rules="[dateInPast]"
                :error-messages="missingMessage('legalRegistrationDate')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalRegistrationDate") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.legalShareCapital"
                type="number"
                min="1"
                step="1"
                :rules="[positiveNumber]"
                :error-messages="missingMessage('legalShareCapital')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalShareCapital") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.legalTvaNumber"
                :label="t('treezorOnboarding.legalTvaNumber')"
              />
            </v-col>
          </v-row>
        </OpeningCard>
        <OpeningCard :title="t('treezorOnboarding.sections.contact')">
          <v-row :gap="[32, 24]">
            <v-col cols="12">
              <v-text-field
                v-model.trim="formData.address1"
                :rules="[maxLength(150)]"
                :error-messages="missingMessage('address1')"
                maxlength="150"
                counter="150"
              >
                <template #label>
                  {{ t("treezorOnboarding.address1") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.address2"
                :rules="[maxLength(150)]"
                maxlength="150"
                counter="150"
                :label="t('treezorOnboarding.address2')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.address3"
                :rules="[maxLength(150)]"
                maxlength="150"
                counter="150"
                :label="t('treezorOnboarding.address3')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.postcode"
                :error-messages="missingMessage('postcode')"
              >
                <template #label>
                  {{ t("treezorOnboarding.postcode") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model.trim="formData.city" :error-messages="missingMessage('city')">
                <template #label>
                  {{ t("treezorOnboarding.city") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="formData.country"
                :error-messages="missingMessage('country')"
                :items="
                  isoCountryItems.filter((item) =>
                    TreezorCountryCodeSchema.options.some((code) => code === item.value),
                  )
                "
                clearable
              >
                <template #label>
                  {{ t("treezorOnboarding.country") }}
                  <span class="text-error">*</span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.trim="formData.phone"
                :rules="[validPhoneNumber]"
                :error-messages="missingMessage('phone')"
                :hint="t('treezorOnboarding.phoneHint')"
                persistent-hint
              >
                <template #label>
                  {{ t("common.phone") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </OpeningCard>
        <OpeningCard :title="t('treezorOnboarding.sections.activity')">
          <v-row :gap="[32, 24]">
            <v-col cols="12" md="6">
              <div class="flex flex-col gap-4 md:flex-row md:items-start">
                <v-select
                  v-model="formData.legalSectorType"
                  :items="legalSectorTypeItems"
                  class="w-full shrink-0 md:w-48"
                  clearable
                  :label="t('treezorOnboarding.legalSectorType')"
                />
                <v-text-field
                  v-model.trim="formData.legalSector"
                  class="min-w-0 md:grow"
                  :rules="[validLegalSector]"
                  :error-messages="missingMessage('legalSector')"
                  :hint="legalSectorHint"
                  persistent-hint
                >
                  <template #label>
                    {{ t("treezorOnboarding.legalSector") }}
                    <span class="text-error">*</span>
                  </template>
                </v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.legalAnnualTurnOver"
                :items="legalAnnualTurnOverItems"
                :error-messages="missingMessage('legalAnnualTurnOver')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalAnnualTurnOver") }}
                  <span class="text-error">*</span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.legalNetIncomeRange"
                :items="legalNetIncomeRangeItems"
                :error-messages="missingMessage('legalNetIncomeRange')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalNetIncomeRange") }}
                  <span class="text-error">*</span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.legalNumberOfEmployeeRange"
                :items="legalNumberOfEmployeeRangeItems"
                :error-messages="missingMessage('legalNumberOfEmployeeRange')"
              >
                <template #label>
                  {{ t("treezorOnboarding.legalNumberOfEmployeeRange") }}
                  <span class="text-error">*</span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12">
              <div class="text-medium-emphasis">
                {{ t("treezorOnboarding.sanctions.activityOutsideEu") }}
                <span class="text-error">*</span>
              </div>
              <v-radio-group
                v-model="formData.activityOutsideEu"
                :error-messages="missingMessage('activityOutsideEu')"
                color="primary"
                inline
                hide-details="auto"
                :aria-label="t('treezorOnboarding.sanctions.activityOutsideEu')"
              >
                <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="1" />
                <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="0" />
              </v-radio-group>
            </v-col>
            <template v-if="formData.activityOutsideEu === 1">
              <v-col cols="12" md="6">
                <div class="text-medium-emphasis">
                  {{ t("treezorOnboarding.sanctions.economicSanctions") }}
                  <span class="text-error">*</span>
                </div>
                <v-radio-group
                  v-model="formData.economicSanctions"
                  :error-messages="missingMessage('economicSanctions')"
                  color="primary"
                  inline
                  hide-details="auto"
                  :aria-label="t('treezorOnboarding.sanctions.economicSanctions')"
                >
                  <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="1" />
                  <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="0" />
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-medium-emphasis">
                  {{ t("treezorOnboarding.sanctions.residentCountriesSanctions") }}
                  <span class="text-error">*</span>
                </div>
                <v-radio-group
                  v-model="formData.residentCountriesSanctions"
                  :error-messages="missingMessage('residentCountriesSanctions')"
                  color="primary"
                  inline
                  hide-details="auto"
                  :aria-label="t('treezorOnboarding.sanctions.residentCountriesSanctions')"
                >
                  <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="1" />
                  <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="0" />
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-medium-emphasis">
                  {{ t("treezorOnboarding.sanctions.involvedSanctions") }}
                  <span class="text-error">*</span>
                </div>
                <v-radio-group
                  v-model="formData.involvedSanctions"
                  :error-messages="missingMessage('involvedSanctions')"
                  color="primary"
                  inline
                  hide-details="auto"
                  :aria-label="t('treezorOnboarding.sanctions.involvedSanctions')"
                >
                  <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="1" />
                  <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="0" />
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-medium-emphasis">
                  {{ t("treezorOnboarding.sanctions.hasSubsidiaries") }}
                  <span class="text-error">*</span>
                </div>
                <v-radio-group
                  v-model="hasSubsidiaries"
                  :error-messages="
                    hasSubsidiaries === null ? missingMessage('entitySanctionsQuestionnaire') : []
                  "
                  color="primary"
                  inline
                  hide-details="auto"
                  :aria-label="t('treezorOnboarding.sanctions.hasSubsidiaries')"
                >
                  <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="1" />
                  <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="0" />
                </v-radio-group>
              </v-col>
              <v-col v-if="hasSubsidiaries === 1" cols="12" md="6">
                <v-select
                  v-model="formData.entitySanctionsQuestionnaire"
                  :items="entitySanctionsQuestionnaireItems"
                  :error-messages="missingMessage('entitySanctionsQuestionnaire')"
                >
                  <template #label>
                    {{ t("treezorOnboarding.sanctions.entitySanctionsQuestionnaire") }}
                    <span class="text-error">*</span>
                  </template>
                </v-select>
              </v-col>
            </template>
          </v-row>
        </OpeningCard>
      </v-form>

      <div class="mt-6 flex justify-end gap-2">
        <v-btn
          type="submit"
          form="company-information-form"
          color="primary"
          variant="tonal"
          :loading="updating"
          :disabled="updating || !canEditKyc"
        >
          {{ t("treezorOnboarding.saveDraft") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-content-save"
          append-icon="mdi-chevron-right"
          :disabled="updating"
          @click="onNext"
        >
          {{ t("common.next") }}
        </v-btn>
      </div>
    </template>
  </template>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast } from "@/store";
import {
  TreezorCountryCodeSchema,
  type TreezorCountryCode,
  type TreezorKycCompanyField,
  type User as TreezorUser,
} from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { computed, reactive, ref, watch } from "vue";
import type { VForm } from "vuetify/components";
import OpeningCard from "../OpeningCard.vue";
import { useTreezorOnboarding } from "../useTreezorOnboarding";

const props = defineProps<{ showMissing: boolean }>();
const emit = defineEmits<{ next: [accountId: string] }>();

const { t } = useI18n();
const { validPhoneNumber, maxLength, dateInPast, required, positiveNumber } = useRules();
const { showSuccessToast, showErrorToast } = useToast();
const {
  accountId,
  canManage,
  canEditKyc,
  account,
  loadingAccount,
  kycReviewReadiness,
  isoCountryItems,
} = useTreezorOnboarding();

const form = ref<VForm>();
const updating = ref(false);
const hasSubsidiaries = ref<0 | 1 | null>(null);

const formData = reactive({
  address1: "",
  address2: "",
  address3: "",
  postcode: "",
  city: "",
  country: null as TreezorCountryCode | null,
  phone: "",
  legalName: "",
  legalRegistrationNumber: "",
  legalForm: "",
  legalRegistrationDate: "",
  legalAnnualTurnOver: null as Exclude<TreezorUser["legalAnnualTurnOver"], ""> | null,
  legalNetIncomeRange: null as Exclude<TreezorUser["legalNetIncomeRange"], ""> | null,
  legalNumberOfEmployeeRange: null as Exclude<TreezorUser["legalNumberOfEmployeeRange"], ""> | null,
  legalSectorType: null as "NAF" | "NACE" | null,
  legalSector: "",
  legalShareCapital: null as number | null,
  legalTvaNumber: "",
  entitySanctionsQuestionnaire: null as 2 | 3 | 4 | null,
  activityOutsideEu: null as Exclude<TreezorUser["activityOutsideEu"], null> | null,
  economicSanctions: null as Exclude<TreezorUser["economicSanctions"], null> | null,
  residentCountriesSanctions: null as Exclude<
    TreezorUser["residentCountriesSanctions"],
    null
  > | null,
  involvedSanctions: null as Exclude<TreezorUser["involvedSanctions"], null> | null,
});

const legalAnnualTurnOverItems = [
  "0-39",
  "40-99",
  "100-249",
  "250-999",
  "1000-2999",
  "3000-9999",
  "10000-99999",
  "100000-*",
] as const satisfies readonly Exclude<TreezorUser["legalAnnualTurnOver"], "">[];

const legalNetIncomeRangeItems = [
  "*--500",
  "-499--150",
  "-149--50",
  "-49--10",
  "-9--1",
  "0-4",
  "5-9",
  "10-49",
  "50-149",
  "150-499",
  "500-*",
] as const satisfies readonly Exclude<TreezorUser["legalNetIncomeRange"], "">[];

const legalNumberOfEmployeeRangeItems = [
  "0",
  "1-9",
  "10-99",
  "100-249",
  "250-*",
] as const satisfies readonly Exclude<TreezorUser["legalNumberOfEmployeeRange"], "">[];

const legalSectorTypeItems = ["NAF", "NACE"] as const;

/** INSEE codes Treezor accepts here: `distributionCountry` is not sent, so it stays FR. */
const legalFormCodes = [
  "5710",
  "5720",
  "5499",
  "5498",
  "5599",
  "5699",
  "6540",
  "6541",
  "5202",
  "5306",
  "5308",
  "6521",
  "6589",
  "5485",
  "5785",
  "6220",
  "5800",
] as const;

const legalFormItems = computed(() => {
  const items = legalFormCodes.map((value) => ({
    title: t(`treezorOnboarding.legalForms.${value}`),
    value,
  }));
  if (formData.legalForm && !legalFormCodes.some((code) => code === formData.legalForm))
    return [...items, { title: formData.legalForm, value: formData.legalForm }];
  return items;
});

const entitySanctionsQuestionnaireItems = computed(() => [
  { title: t("treezorOnboarding.sanctions.scope.subsidiaries100"), value: 2 as const },
  { title: t("treezorOnboarding.sanctions.scope.subsidiaries50"), value: 3 as const },
  { title: t("treezorOnboarding.sanctions.scope.listOfSubsidiaries"), value: 4 as const },
]);

const legalSectorHint = computed(() => {
  if (formData.legalSectorType === "NACE") return t("treezorOnboarding.legalSectorHintNace");
  if (formData.legalSectorType === "NAF") return t("treezorOnboarding.legalSectorHintNaf");
  return t("treezorOnboarding.legalSectorHint");
});

// Missing values only show once the step was left, and never block saving.
const missingMessage = (field: TreezorKycCompanyField) =>
  props.showMissing &&
  kycReviewReadiness.value.missingCompanyFields.includes(field) &&
  required(formData[field]) !== true
    ? t("errors.required")
    : [];

const validLegalSector = (value: unknown) => {
  if (!value) return true;
  if (typeof value !== "string") return t("errors.required");
  if (formData.legalSectorType === "NACE") {
    return /^\d{4}$/.test(value) || t("errors.invalidLegalSectorNace");
  }
  if (formData.legalSectorType === "NAF") {
    return /^\d{4}[A-Z]$/.test(value) || t("errors.invalidLegalSectorNaf");
  }
  return true;
};

watch(
  () => account.value?.treezorCompany,
  (company) => {
    if (!company) return;
    formData.address1 = company.address1 ?? "";
    formData.address2 = company.address2 ?? "";
    formData.address3 = company.address3 ?? "";
    formData.postcode = company.postcode ?? "";
    formData.city = company.city ?? "";
    const parsedCountry = TreezorCountryCodeSchema.safeParse(company.country);
    formData.country = parsedCountry.success ? parsedCountry.data : null;
    formData.phone = company.phone ?? "";
    formData.legalName = company.legalName ?? "";
    formData.legalRegistrationNumber = company.legalRegistrationNumber ?? "";
    formData.legalForm = company.legalForm ?? "";
    formData.legalRegistrationDate = company.legalRegistrationDate ?? "";
    formData.legalAnnualTurnOver = company.legalAnnualTurnOver ? company.legalAnnualTurnOver : null;
    formData.legalNetIncomeRange = company.legalNetIncomeRange ? company.legalNetIncomeRange : null;
    formData.legalNumberOfEmployeeRange = company.legalNumberOfEmployeeRange
      ? company.legalNumberOfEmployeeRange
      : null;
    formData.legalSectorType =
      company.legalSectorType === "NAF" || company.legalSectorType === "NACE"
        ? company.legalSectorType
        : null;
    formData.legalSector = company.legalSector ?? "";
    formData.legalShareCapital = company.legalShareCapital || null;
    formData.legalTvaNumber = company.legalTvaNumber ?? "";
    formData.entitySanctionsQuestionnaire =
      company.entitySanctionsQuestionnaire === 2 ||
      company.entitySanctionsQuestionnaire === 3 ||
      company.entitySanctionsQuestionnaire === 4
        ? company.entitySanctionsQuestionnaire
        : null;
    formData.activityOutsideEu =
      company.activityOutsideEu === 0 || company.activityOutsideEu === 1
        ? company.activityOutsideEu
        : null;
    formData.economicSanctions =
      company.economicSanctions === 0 || company.economicSanctions === 1
        ? company.economicSanctions
        : null;
    formData.residentCountriesSanctions =
      company.residentCountriesSanctions === 0 || company.residentCountriesSanctions === 1
        ? company.residentCountriesSanctions
        : null;
    formData.involvedSanctions =
      company.involvedSanctions === 0 || company.involvedSanctions === 1
        ? company.involvedSanctions
        : null;
    hasSubsidiaries.value = null;
    if (company.activityOutsideEu === 1) {
      if (company.entitySanctionsQuestionnaire === 1) hasSubsidiaries.value = 0;
      if (
        company.entitySanctionsQuestionnaire === 2 ||
        company.entitySanctionsQuestionnaire === 3 ||
        company.entitySanctionsQuestionnaire === 4
      )
        hasSubsidiaries.value = 1;
    }
  },
  { immediate: true },
);

const save = async () => {
  if (!canEditKyc.value) return true;
  const result = await form.value?.validate();
  if (!result?.valid) return false;

  let economicSanctions = formData.economicSanctions;
  let residentCountriesSanctions = formData.residentCountriesSanctions;
  let involvedSanctions = formData.involvedSanctions;
  let entitySanctionsQuestionnaire: 1 | 2 | 3 | 4 | undefined;
  if (formData.activityOutsideEu === 0) {
    entitySanctionsQuestionnaire = 1;
    economicSanctions = 0;
    residentCountriesSanctions = 0;
    involvedSanctions = 0;
  } else if (hasSubsidiaries.value === 0) entitySanctionsQuestionnaire = 1;
  else if (hasSubsidiaries.value === 1)
    entitySanctionsQuestionnaire = formData.entitySanctionsQuestionnaire ?? undefined;

  const companyUpdate = {
    accountId: accountId.value,
    legalName: formData.legalName,
    legalRegistrationNumber: formData.legalRegistrationNumber,
    legalForm: formData.legalForm,
    legalRegistrationDate: formData.legalRegistrationDate,
    legalAnnualTurnOver: formData.legalAnnualTurnOver ?? ("" as const),
    legalNetIncomeRange: formData.legalNetIncomeRange ?? ("" as const),
    legalNumberOfEmployeeRange: formData.legalNumberOfEmployeeRange ?? ("" as const),
    legalSectorType: formData.legalSectorType,
    legalSector: formData.legalSector,
    legalShareCapital: formData.legalShareCapital ?? 0,
    legalTvaNumber: formData.legalTvaNumber,
    phone: formData.phone,
    address1: formData.address1,
    address2: formData.address2,
    address3: formData.address3,
    postcode: formData.postcode,
    city: formData.city,
    country: formData.country ?? ("" as const),
    entitySanctionsQuestionnaire,
    activityOutsideEu: formData.activityOutsideEu,
    economicSanctions,
    residentCountriesSanctions,
    involvedSanctions,
  };

  updating.value = true;
  try {
    await call("updateTreezorCompany")(companyUpdate);
    showSuccessToast();
    return true;
  } catch (cause) {
    const error = new Error("Error updating Treezor company", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
    return false;
  } finally {
    updating.value = false;
  }
};

const onNext = async () => {
  const savedAccountId = accountId.value;
  if (await save()) emit("next", savedAccountId);
};
</script>
